// Typed personal-account membership, invitation, and coaching operations.
import { httpClient } from "@bw/shared/api";
import type {
  ClubCoachProfileCreate,
  ClubCoachProfilePublicResponse,
  ClubCoachProfileResponse,
  ClubCoachProfileUpdate,
  StaffOffboardingChecklist as GeneratedStaffOffboardingChecklist,
  StaffOffboardingWorkItem as GeneratedStaffOffboardingWorkItem,
  Schema,
} from "@bw/shared/types/generated";

export type PersonalStaffInvitation = Schema["StaffPersonalInvitationView"];
export type PersonalStaffInvitationCreate = Schema["StaffPersonalInvitationCreate"];
export type StaffInvitationCandidate = Schema["StaffInvitationCandidate"];

export function searchStaffInvitationCandidates(orgId: string, query: string, signal?: AbortSignal): Promise<StaffInvitationCandidate[]> {
  const params = new URLSearchParams({ org_id: orgId, q: query, limit: "20" });
  return httpClient.get(`/staff/invitation-candidates?${params}`, { signal });
}

export function listMyStaffInvitations(): Promise<PersonalStaffInvitation[]> {
  return httpClient.get("/staff/personal-invitations/mine");
}

export function listStaffInvitations(orgId: string): Promise<PersonalStaffInvitation[]> {
  return httpClient.get(`/staff/personal-invitations?org_id=${encodeURIComponent(orgId)}`);
}

export function revokeStaffInvitation(id: string, expectedRevision: number, key: string): Promise<PersonalStaffInvitation> {
  return httpClient.post(`/staff/personal-invitations/${encodeURIComponent(id)}/revoke`, {
    expected_revision: expectedRevision,
  }, { headers: { "Idempotency-Key": key } });
}

export function getStaffInvitation(id: string): Promise<PersonalStaffInvitation> {
  return httpClient.get(`/staff/personal-invitations/${encodeURIComponent(id)}`);
}

export function createStaffInvitation(orgId: string, body: PersonalStaffInvitationCreate, key: string): Promise<PersonalStaffInvitation> {
  return httpClient.post(`/staff/personal-invitations?org_id=${encodeURIComponent(orgId)}`, body, {
    headers: { "Idempotency-Key": key },
  });
}

export function acceptStaffInvitation(id: string, expectedRevision: number, key: string): Promise<PersonalStaffInvitation> {
  return httpClient.post(`/staff/personal-invitations/${encodeURIComponent(id)}/accept`, {
    expected_revision: expectedRevision,
  }, { headers: { "Idempotency-Key": key } });
}

export type TeamPerson = Schema["StaffPersonalMemberSummary"];
export type StaffOffboardingWorkItem = GeneratedStaffOffboardingWorkItem;
export type StaffOffboardingChecklist = GeneratedStaffOffboardingChecklist;

export function listTeamPeople(orgId: string): Promise<TeamPerson[]> {
  return httpClient.get<TeamPerson[]>(
    `/staff/personal-members?org_id=${encodeURIComponent(orgId)}`,
  );
}
export function setTeamPersonStaffManagement(
  orgId: string,
  memberId: string,
  enabled: boolean,
  expectedRevision: number,
): Promise<TeamPerson> {
  return httpClient.put<TeamPerson>(
    `/staff/personal-members/${encodeURIComponent(memberId)}/staff-management?org_id=${encodeURIComponent(orgId)}`,
    { enabled, expected_revision: expectedRevision },
  );
}
export function suspendTeamPerson(
  orgId: string,
  memberId: string,
  expectedRevision: number,
): Promise<TeamPerson> {
  return httpClient.post<TeamPerson>(
    `/staff/personal-members/${encodeURIComponent(memberId)}/suspend?org_id=${encodeURIComponent(orgId)}`,
    { expected_revision: expectedRevision },
  );
}
export function completeTeamPersonOffboarding(
  orgId: string,
  memberId: string,
  expectedRevision: number,
): Promise<TeamPerson> {
  return httpClient.post<TeamPerson>(
    `/staff/personal-members/${encodeURIComponent(memberId)}/complete-offboarding?org_id=${encodeURIComponent(orgId)}`,
    { expected_revision: expectedRevision },
  );
}
export function getTeamPersonOffboarding(
  orgId: string,
  memberId: string,
  continuation?: StaffOffboardingChecklist["continuation"],
): Promise<StaffOffboardingChecklist> {
  const query = new URLSearchParams({ org_id: orgId });
  if (continuation?.program_offset != null)
    query.set("program_offset", String(continuation.program_offset));
  if (continuation?.lesson_request_offset != null)
    query.set(
      "lesson_request_offset",
      String(continuation.lesson_request_offset),
    );
  if (continuation?.social_offset != null)
    query.set("social_offset", String(continuation.social_offset));
  return httpClient.get<StaffOffboardingChecklist>(
    `/staff/personal-members/${encodeURIComponent(memberId)}/offboarding?${query.toString()}`,
  );
}
export function replaceTeamPersonAssignments(
  orgId: string,
  memberId: string,
  assignments: Schema["StaffPersonalMemberAssignments"]["assignments"],
  expectedRevision: number,
): Promise<TeamPerson> {
  return httpClient.put<TeamPerson>(
    `/staff/personal-members/${encodeURIComponent(memberId)}/assignments?org_id=${encodeURIComponent(orgId)}`,
    { assignments, expected_revision: expectedRevision },
  );
}

export type ClubCoachProfileDraft = ClubCoachProfileResponse;
export type ClubCoachProfileWrite = ClubCoachProfileUpdate;
export type ClubCoachExperienceSummary = {
  coach_profile_id: string;
  coaching_minutes: number;
  sessions_taught: number;
  unique_students: number;
  source_kind: "club_attested";
  clubs: {
    org_id: string;
    coaching_minutes: number;
    sessions_taught: number;
    unique_students: number;
    evidence_revision: number;
    source_kind: "club_attested";
  }[];
};
export type CoachExperienceClaim = {
  claim_id: string;
  coach_profile_id: string;
  source_org_id: string;
  coach_display_name: string;
  status: "pending_club" | "approved" | "rejected" | "platform_review";
  claimant_display_name: string;
  identity_proof_kind: "firebase_verified_email" | "platform_recovery";
  identity_proof_checked_at: string;
  visibility: "private" | "shared_clubs";
  revision: number;
  decision_reason: string | null;
  created_at: string;
  resolved_at: string | null;
};

export function listCoachExperienceClaims(
  orgId: string,
): Promise<CoachExperienceClaim[]> {
  return httpClient.get<CoachExperienceClaim[]>(
    `/coach-experience/business/claims?org_id=${encodeURIComponent(orgId)}&status=pending_club`,
  );
}

export function decideCoachExperienceClaim(
  orgId: string,
  claim: CoachExperienceClaim,
  decision: "approve" | "reject",
  reason: string,
): Promise<CoachExperienceClaim> {
  return httpClient.post<CoachExperienceClaim>(
    `/coach-experience/business/claims/${encodeURIComponent(claim.claim_id)}/decision?org_id=${encodeURIComponent(orgId)}`,
    {
      decision,
      reason,
      expected_revision: claim.revision,
      idempotency_key: crypto.randomUUID(),
    },
  );
}
export type CoachExperienceShare = {
  share_id: string;
  claim_id: string;
  coach_display_name: string;
  destination_org_id: string;
  destination_coach_profile_id: string | null;
  status: "pending_destination" | "active" | "rejected" | "revoked";
  scopes: (
    | "coaching_minutes"
    | "sessions_taught"
    | "per_club_unique_students"
    | "achievements"
  )[];
  revision: number;
  created_at: string;
  resolved_at: string | null;
};
export function listCoachExperienceShares(
  orgId: string,
): Promise<CoachExperienceShare[]> {
  return httpClient.get<CoachExperienceShare[]>(
    `/coach-experience/business/shares?org_id=${encodeURIComponent(orgId)}&status=pending_destination`,
  );
}
export function decideCoachExperienceShare(
  orgId: string,
  share: CoachExperienceShare,
  decision: "approve" | "reject",
  reason: string,
): Promise<CoachExperienceShare> {
  return httpClient.post<CoachExperienceShare>(
    `/coach-experience/business/shares/${encodeURIComponent(share.share_id)}/decision?org_id=${encodeURIComponent(orgId)}`,
    {
      decision,
      reason,
      expected_revision: share.revision,
      idempotency_key: crypto.randomUUID(),
    },
  );
}
export type SharedCoachExperience = {
  share_id: string;
  coach_display_name: string;
  coaching_minutes: number | null;
  sessions_taught: number | null;
  per_club_unique_students: number | null;
  achievements:
    | { achievement_id: string; title: string; provenance: string }[]
    | null;
  scopes: string[];
  evidence_revision: number;
};
export function listSharedCoachExperience(
  orgId: string,
): Promise<SharedCoachExperience[]> {
  return httpClient.get<SharedCoachExperience[]>(
    `/coach-experience/business/shared-experience?org_id=${encodeURIComponent(orgId)}`,
  );
}

export function createClubCoachProfile(
  orgId: string,
  body: ClubCoachProfileCreate,
): Promise<ClubCoachProfileDraft> {
  return httpClient.post<ClubCoachProfileDraft>(
    `/coaches/club-profiles?org_id=${encodeURIComponent(orgId)}`,
    body,
  );
}

export function updateClubCoachProfile(
  orgId: string,
  profileId: string,
  body: ClubCoachProfileWrite,
): Promise<ClubCoachProfileDraft> {
  return httpClient.put<ClubCoachProfileDraft>(
    `/coaches/club-profiles/${encodeURIComponent(profileId)}?org_id=${encodeURIComponent(orgId)}`,
    body,
  );
}

export function listClubCoachProfiles(
  orgId: string,
): Promise<ClubCoachProfileDraft[]> {
  return httpClient.get<ClubCoachProfileDraft[]>(
    `/coaches/club-profiles?org_id=${encodeURIComponent(orgId)}`,
  );
}

export function listAssignableClubCoaches(
  facilityId: string,
  signal?: AbortSignal,
): Promise<ClubCoachProfilePublicResponse[]> {
  return httpClient.get<ClubCoachProfilePublicResponse[]>(
    `/coaches/club-profiles/assignment-candidates?facility_id=${encodeURIComponent(facilityId)}`,
    { signal },
  );
}

export function getMyClubCoachProfile(orgId: string): Promise<ClubCoachProfileDraft> {
  return httpClient.get<ClubCoachProfileDraft>(`/coaches/club-profiles/mine?org_id=${encodeURIComponent(orgId)}`);
}

export function getClubCoachExperience(
  profileId: string,
): Promise<ClubCoachExperienceSummary> {
  return httpClient.get<ClubCoachExperienceSummary>(
    `/coaches/club-profiles/${encodeURIComponent(profileId)}/experience`,
  );
}

export function publishClubCoachProfile(
  orgId: string,
  profileId: string,
): Promise<ClubCoachProfileDraft> {
  return httpClient.post<ClubCoachProfileDraft>(
    `/coaches/club-profiles/${encodeURIComponent(profileId)}/publish?org_id=${encodeURIComponent(orgId)}`,
    {},
  );
}

export function archiveClubCoachProfile(
  orgId: string,
  profileId: string,
): Promise<ClubCoachProfileDraft> {
  return httpClient.post<ClubCoachProfileDraft>(
    `/coaches/club-profiles/${encodeURIComponent(profileId)}/archive?org_id=${encodeURIComponent(orgId)}`,
    {},
  );
}
